import { useCallback, useMemo, useState } from "react";
import type { AutoCombineEntryV2, EntryMiniTransaction } from "@/shared/cache";

export interface MiniTransactionSelection {
	selectedMiniIds: Set<string>;
	selectedMiniTransactions: EntryMiniTransaction[];
	hasMiniSelection: boolean;
	/** Entry group ids that currently hold at least one selected source entry. */
	parentIdsWithSelection: Set<string>;
	toggleMini: (id: string) => void;
	setMiniSelected: (id: string, selected: boolean) => void;
	clearMiniSelection: () => void;
}

/**
 * Tracks which source entries are selected, independent of entry-group row
 * selection. Derives the selected source-entry objects and the set of entry
 * groups that contain them (used to forbid moving a source entry into its own
 * group).
 * Ids that vanish after a refetch are ignored on derivation.
 */
export function useMiniTransactionSelection(
	entries: AutoCombineEntryV2[],
): MiniTransactionSelection {
	const [selectedMiniIds, setSelectedMiniIds] = useState<Set<string>>(
		() => new Set(),
	);

	const { miniById, parentByMiniId } = useMemo(() => {
		const miniById = new Map<string, EntryMiniTransaction>();
		const parentByMiniId = new Map<string, string>();
		for (const entry of entries) {
			for (const mini of entry.miniTransactions) {
				miniById.set(mini.id, mini);
				parentByMiniId.set(mini.id, entry.id);
			}
		}
		return { miniById, parentByMiniId };
	}, [entries]);

	const selectedMiniTransactions = useMemo(() => {
		const out: EntryMiniTransaction[] = [];
		for (const id of selectedMiniIds) {
			const mini = miniById.get(id);
			if (mini) out.push(mini);
		}
		return out;
	}, [selectedMiniIds, miniById]);

	const parentIdsWithSelection = useMemo(() => {
		const out = new Set<string>();
		for (const id of selectedMiniIds) {
			const parentId = parentByMiniId.get(id);
			if (parentId) out.add(parentId);
		}
		return out;
	}, [selectedMiniIds, parentByMiniId]);

	const toggleMini = useCallback((id: string) => {
		setSelectedMiniIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const setMiniSelected = useCallback((id: string, selected: boolean) => {
		setSelectedMiniIds((prev) => {
			if (selected === prev.has(id)) return prev;
			const next = new Set(prev);
			if (selected) next.add(id);
			else next.delete(id);
			return next;
		});
	}, []);

	const clearMiniSelection = useCallback(() => {
		setSelectedMiniIds((prev) => (prev.size === 0 ? prev : new Set()));
	}, []);

	return {
		selectedMiniIds,
		selectedMiniTransactions,
		hasMiniSelection: selectedMiniIds.size > 0,
		parentIdsWithSelection,
		toggleMini,
		setMiniSelected,
		clearMiniSelection,
	};
}
